import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, X, MapPin, GraduationCap, Building2, Sparkles, Calendar,
  ArrowUpRight, Filter as FilterIcon, BookOpen, Eye, CheckCircle2,
  Award, Wallet,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { listAllUniversities } from '../data/universityData';
import { trackCtaClick } from '@/services/analytics';

/**
 * PartnerDirectory — searchable, filterable list of Zafco's partner
 * universities (currently surfaces UK, Germany, and Dubai/UAE entries
 * from universityData.js — Canada, Australia, Ireland surfaced
 * contextually during counselling sessions).
 *
 * UX:
 *   - free-text search (name + city + popular programs)
 *   - country pill filter (All + per-country chips with live counts)
 *   - type filter (All / Public / Private)
 *   - 12 cards per page with "Show 12 more" pagination
 *   - empty-state with CTA back to the counsellor
 *
 * Embedded on /study-abroad/global-partnerships/.
 */

const COUNTRY_LABELS = {
  uk:      { label: 'UK',         flag: '🇬🇧', hubHref: '/study-abroad/uk/' },
  germany: { label: 'Germany',    flag: '🇩🇪', hubHref: '/study-abroad/germany/' },
  dubai:   { label: 'Dubai/UAE',  flag: '🇦🇪', hubHref: '/study-abroad/dubai/' },
};

const PAGE_SIZE = 12;

const PartnerCard = ({ u, onSelect }) => {
  const country = COUNTRY_LABELS[u.countrySlug] || { label: u.country, flag: '🌐' };
  const isPrivate = (u.type || '').toLowerCase().includes('private');
  return (
    <article
      className="group relative rounded-2xl bg-white border border-slate-200 hover:border-[#C5A028]/50 hover:shadow-md hover:-translate-y-0.5 transition-all p-5 flex flex-col"
      data-testid={`pd-card-${u.slug}`}
    >
      {/* Top — flag + country */}
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-1.5 text-[10.5px] font-bold tracking-[0.18em] text-slate-500 uppercase">
          <span className="text-base leading-none">{country.flag}</span>
          {country.label}
        </div>
        <span className={`text-[9px] font-bold tracking-[0.18em] uppercase px-2 py-0.5 rounded-full ${
          isPrivate
            ? 'bg-amber-50 text-amber-800 border border-amber-200'
            : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
        }`}>
          {isPrivate ? 'Private' : 'Public'}
        </span>
      </div>

      {/* Name + city */}
      <h3 className="text-base font-bold font-syne text-[#003087] leading-snug mb-1.5 line-clamp-2">
        {u.name}
      </h3>
      <div className="inline-flex items-start gap-1.5 text-xs text-slate-500 mb-3">
        <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
        <span className="line-clamp-1">{u.city}</span>
      </div>

      {/* Highlights row — ranking */}
      {u.ranking && (
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#003087]/6 text-[10.5px] font-semibold text-[#003087] mb-3 self-start max-w-full">
          <Sparkles className="w-3 h-3 text-[#C5A028] shrink-0" />
          <span className="line-clamp-1">{u.ranking}</span>
        </div>
      )}

      {/* Programs */}
      {Array.isArray(u.popularPrograms) && u.popularPrograms.length > 0 && (
        <div className="mb-3">
          <div className="text-[9.5px] font-bold tracking-[0.18em] text-slate-400 uppercase mb-1.5 inline-flex items-center gap-1">
            <BookOpen className="w-3 h-3" /> Popular Programs
          </div>
          <div className="flex flex-wrap gap-1.5">
            {u.popularPrograms.slice(0, 3).map(p => (
              <span key={p} className="text-[10.5px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{p}</span>
            ))}
            {u.popularPrograms.length > 3 && (
              <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-slate-50 text-slate-500 border border-slate-200">
                +{u.popularPrograms.length - 3}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Meta footer — IELTS + Intakes */}
      <div className="mt-auto grid grid-cols-2 gap-2 pt-3 border-t border-slate-100">
        {u.ieltsRequirement && (
          <div>
            <div className="text-[9.5px] font-bold tracking-[0.18em] text-slate-400 uppercase">IELTS</div>
            <div className="text-xs font-bold text-[#003087]">{u.ieltsRequirement}</div>
          </div>
        )}
        {Array.isArray(u.intakes) && u.intakes.length > 0 && (
          <div>
            <div className="text-[9.5px] font-bold tracking-[0.18em] text-slate-400 uppercase inline-flex items-center gap-1">
              <Calendar className="w-2.5 h-2.5" /> Intakes
            </div>
            <div className="text-xs font-bold text-[#003087] truncate" title={u.intakes.join(', ')}>
              {u.intakes.length} / year
            </div>
          </div>
        )}
      </div>

      {/* CTAs — View Details (secondary) + Check Eligibility (primary) */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onSelect(u)}
          className="inline-flex items-center justify-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-slate-600 hover:text-[#003087] bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 rounded-full py-2 px-3 transition-all"
          data-testid={`pd-card-view-${u.slug}`}
          aria-label={`View details for ${u.name}`}
        >
          <Eye className="w-3.5 h-3.5" /> View
        </button>
        <Link
          to={`/study-abroad/free-profile-evaluation/?source=partner-directory&partner=${encodeURIComponent(u.slug)}`}
          onClick={() => trackCtaClick('partner-directory-card-eligibility', { partner: u.slug, country: u.countrySlug })}
          className="inline-flex items-center justify-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-black bg-[#C5A028] hover:bg-[#EAB308] rounded-full py-2 px-3 transition-all"
          data-testid={`pd-card-cta-${u.slug}`}
        >
          Eligibility <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    </article>
  );
};

const PartnerDetailSheet = ({ u, open, onOpenChange }) => {
  if (!u) {
    return <Sheet open={open} onOpenChange={onOpenChange} />;
  }
  const country = COUNTRY_LABELS[u.countrySlug] || { label: u.country, flag: '🌐' };
  const isPrivate = (u.type || '').toLowerCase().includes('private');
  const tuition = u.averageTuition || {};
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg overflow-y-auto p-0"
        data-testid="pd-detail-sheet"
      >
        {/* Gradient header band */}
        <div
          className="relative px-6 pt-7 pb-5 text-white"
          style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
        >
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-12 w-48 h-48 rounded-full bg-[#C5A028]/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/8 border border-white/15 text-[10px] font-bold tracking-[0.22em] uppercase text-[#FACC15]">
                <span className="text-base leading-none">{country.flag}</span>{country.label}
              </span>
              <span className={`text-[9.5px] font-bold tracking-[0.22em] uppercase px-2 py-0.5 rounded-full ${
                isPrivate ? 'bg-amber-100/15 text-amber-200 border border-amber-300/30' : 'bg-emerald-500/15 text-emerald-200 border border-emerald-400/30'
              }`}>
                {isPrivate ? 'Private' : 'Public'}
              </span>
            </div>
            <SheetHeader>
              <SheetTitle className="text-xl sm:text-2xl font-bold font-syne text-white leading-tight !text-left">
                {u.name}
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-300 !text-left flex items-start gap-1.5">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#FACC15]" /> {u.city}
              </SheetDescription>
            </SheetHeader>
            {u.ranking && (
              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/40 text-[10.5px] font-semibold text-[#FACC15]">
                <Sparkles className="w-3 h-3" /> {u.ranking}
              </div>
            )}
          </div>
        </div>

        {/* Body content */}
        <div className="px-6 py-6 space-y-6">
          {/* Quick facts grid */}
          <div className="grid grid-cols-2 gap-3" data-testid="pd-sheet-facts">
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-1.5">
                <GraduationCap className="w-3 h-3 text-[#C5A028]" /> IELTS
              </div>
              <div className="text-base font-bold font-syne text-[#003087]">{u.ieltsRequirement || '—'}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-1.5">
                <Wallet className="w-3 h-3 text-[#C5A028]" /> Tuition
              </div>
              <div className="text-sm font-bold font-syne text-[#003087] leading-tight">
                {tuition.amount ? `${tuition.amount} ${tuition.currency || ''}` : '—'}
              </div>
              {tuition.period && (
                <div className="text-[10px] text-slate-500 mt-0.5">per {tuition.period}</div>
              )}
            </div>
          </div>

          {/* Intakes */}
          {Array.isArray(u.intakes) && u.intakes.length > 0 && (
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-2.5">
                <Calendar className="w-3 h-3 text-[#C5A028]" /> Intakes
              </div>
              <div className="flex flex-wrap gap-1.5">
                {u.intakes.map(i => (
                  <span key={i} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#003087]/8 text-[#003087] border border-[#003087]/15">{i}</span>
                ))}
              </div>
            </div>
          )}

          {/* Programs */}
          {Array.isArray(u.popularPrograms) && u.popularPrograms.length > 0 && (
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-2.5">
                <BookOpen className="w-3 h-3 text-[#C5A028]" /> Popular Programs
              </div>
              <div className="flex flex-wrap gap-1.5">
                {u.popularPrograms.map(p => (
                  <span key={p} className="text-xs px-2.5 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200">{p}</span>
                ))}
              </div>
            </div>
          )}

          {/* Highlights */}
          {Array.isArray(u.highlights) && u.highlights.length > 0 && (
            <div data-testid="pd-sheet-highlights">
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-2.5">
                <Award className="w-3 h-3 text-[#C5A028]" /> Why this Partner
              </div>
              <ul className="space-y-2">
                {u.highlights.map(h => (
                  <li key={h} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* CTAs */}
          <div className="pt-3 border-t border-slate-200 grid gap-2">
            <Link
              to={`/study-abroad/free-profile-evaluation/?source=partner-directory-sheet&partner=${encodeURIComponent(u.slug)}`}
              onClick={() => { trackCtaClick('partner-directory-sheet-eligibility', { partner: u.slug, country: u.countrySlug }); onOpenChange(false); }}
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-5 py-3 rounded-full transition-all shadow-md shadow-[#C5A028]/30"
              data-testid="pd-sheet-eligibility-cta"
            >
              Check My Eligibility for {u.name.split(' ').slice(0, 3).join(' ')}{u.name.split(' ').length > 3 ? '…' : ''} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/study-abroad/book-counselling/?source=partner-directory-sheet&partner=${encodeURIComponent(u.slug)}`}
              onClick={() => { trackCtaClick('partner-directory-sheet-counsellor', { partner: u.slug, country: u.countrySlug }); onOpenChange(false); }}
              className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-[#003087] font-semibold text-sm px-5 py-3 rounded-full border border-slate-200 transition-all"
              data-testid="pd-sheet-counsellor-cta"
            >
              Talk to a Counsellor First
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const PartnerDirectory = () => {
  const all = useMemo(() => listAllUniversities(), []);

  const [q, setQ]                 = useState('');
  const [country, setCountry]     = useState('all');
  const [unitype, setUnitype]     = useState('all'); // all | public | private
  const [page, setPage]           = useState(1);
  const [selectedUni, setSelectedUni] = useState(null);
  const [sheetOpen, setSheetOpen]     = useState(false);

  const openDetails = (u) => { setSelectedUni(u); setSheetOpen(true); };

  // Country counts (live, on the active list — pre-search but post-typeFilter)
  const countryCounts = useMemo(() => {
    const map = { all: all.length };
    for (const u of all) {
      const slug = u.countrySlug || 'other';
      map[slug] = (map[slug] || 0) + 1;
    }
    return map;
  }, [all]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return all.filter(u => {
      if (country !== 'all' && u.countrySlug !== country) return false;
      if (unitype !== 'all') {
        const t = (u.type || '').toLowerCase();
        if (unitype === 'public'  && !t.includes('public'))  return false;
        if (unitype === 'private' && !t.includes('private')) return false;
      }
      if (!ql) return true;
      const hay = [
        u.name, u.city, u.country, u.ranking,
        ...(Array.isArray(u.popularPrograms) ? u.popularPrograms : []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(ql);
    });
  }, [all, q, country, unitype]);

  // Reset page when filters change
  const visible = filtered.slice(0, PAGE_SIZE * page);
  const hasMore = visible.length < filtered.length;

  const applyCountry = (slug) => { setCountry(slug); setPage(1); };
  const applyType    = (t)    => { setUnitype(t);   setPage(1); };
  const onQuery      = (e)    => { setQ(e.target.value); setPage(1); };

  const countryPills = [
    { slug: 'all',     label: 'All Partners',  flag: '🌍' },
    { slug: 'uk',      ...COUNTRY_LABELS.uk },
    { slug: 'germany', ...COUNTRY_LABELS.germany },
    { slug: 'dubai',   ...COUNTRY_LABELS.dubai },
  ];

  return (
    <section className="bg-slate-50 py-14 lg:py-20" data-testid="partner-directory">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mb-7 lg:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C5A028]/12 border border-[#C5A028]/35 text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-3">
            Partner Directory · Live
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-[36px] font-bold font-syne text-[#003087] leading-tight mb-3">
            Search Our Active Partner Universities.
          </h2>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
            Browse the live directory. Filter by country and institution type, or search for a course or city. Every name below is an actively maintained Zafco partner — Canada, Australia, and Ireland partners are surfaced contextually during your counselling session.
          </p>
        </div>

        {/* Controls */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 mb-6 shadow-sm" data-testid="pd-controls">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input
              type="text"
              value={q}
              onChange={onQuery}
              placeholder="Search by university, city, or program (e.g. MBA, Cambridge, Computer Science)"
              className="w-full h-12 pl-11 pr-10 rounded-xl bg-slate-50 border border-slate-200 text-sm placeholder:text-slate-400 focus:bg-white focus:border-[#C5A028] focus:outline-none focus:ring-2 focus:ring-[#C5A028]/20 transition-all"
              data-testid="pd-search-input"
              aria-label="Search partner universities"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setQ(''); setPage(1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors"
                aria-label="Clear search"
                data-testid="pd-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Country pills */}
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="pd-country-filters">
            <span className="text-[10px] font-bold tracking-[0.22em] text-slate-400 uppercase mr-1 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Country
            </span>
            {countryPills.map(p => {
              const active = country === p.slug;
              const count = p.slug === 'all' ? countryCounts.all : (countryCounts[p.slug] || 0);
              return (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => applyCountry(p.slug)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    active
                      ? 'bg-[#003087] text-white border-[#003087] shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-[#C5A028]/50 hover:bg-amber-50/40'
                  }`}
                  aria-pressed={active}
                  data-testid={`pd-country-pill-${p.slug}`}
                >
                  <span className="text-base leading-none">{p.flag}</span>
                  {p.label}
                  <span className={`text-[10px] font-bold ${active ? 'text-[#FACC15]' : 'text-slate-400'}`}>{count}</span>
                </button>
              );
            })}
          </div>

          {/* Type filter */}
          <div className="flex flex-wrap items-center gap-2" data-testid="pd-type-filters">
            <span className="text-[10px] font-bold tracking-[0.22em] text-slate-400 uppercase mr-1 inline-flex items-center gap-1">
              <Building2 className="w-3 h-3" /> Type
            </span>
            {[
              { k: 'all',     label: 'All' },
              { k: 'public',  label: 'Public' },
              { k: 'private', label: 'Private' },
            ].map(t => {
              const active = unitype === t.k;
              return (
                <button
                  key={t.k}
                  type="button"
                  onClick={() => applyType(t.k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    active
                      ? 'bg-[#C5A028] text-black border-[#C5A028] shadow-sm'
                      : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-[#C5A028]/50 hover:bg-amber-50/40'
                  }`}
                  aria-pressed={active}
                  data-testid={`pd-type-pill-${t.k}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results meta */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5 text-xs">
          <div className="inline-flex items-center gap-1.5 text-slate-500" data-testid="pd-result-meta">
            <FilterIcon className="w-3.5 h-3.5" />
            Showing <strong className="text-[#003087] font-bold">{visible.length}</strong> of <strong className="text-[#003087] font-bold">{filtered.length}</strong> partner universit{filtered.length === 1 ? 'y' : 'ies'}
          </div>
          {country !== 'all' && COUNTRY_LABELS[country] && (
            <Link
              to={COUNTRY_LABELS[country].hubHref}
              className="inline-flex items-center gap-1 text-[#003087] font-bold hover:text-[#C5A028] transition-colors"
              data-testid="pd-country-hub-link"
            >
              <GraduationCap className="w-3.5 h-3.5" /> Open {COUNTRY_LABELS[country].label} hub <ArrowUpRight className="w-3 h-3" />
            </Link>
          )}
        </div>

        {/* Grid */}
        {visible.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center" data-testid="pd-empty-state">
            <p className="text-sm text-slate-600 mb-4">
              No partner universities matched your filters.
            </p>
            <Link
              to="/study-abroad/book-counselling/?source=partner-directory-empty"
              className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-5 py-2.5 rounded-full transition-all"
              data-testid="pd-empty-cta"
            >
              Talk to a Counsellor — we may have a partner not yet listed <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pd-grid">
            {visible.map(u => <PartnerCard key={u.slug} u={u} onSelect={openDetails} />)}
          </div>
        )}

        {/* Pagination */}
        {hasMore && (
          <div className="mt-7 flex justify-center">
            <button
              type="button"
              onClick={() => setPage(p => p + 1)}
              className="inline-flex items-center gap-2 bg-white border-2 border-[#003087]/15 hover:border-[#C5A028] text-[#003087] hover:text-[#C5A028] font-bold text-sm px-6 py-3 rounded-full transition-all"
              data-testid="pd-show-more"
            >
              Show {Math.min(PAGE_SIZE, filtered.length - visible.length)} more
            </button>
          </div>
        )}
      </div>

      {/* Slide-over detail panel */}
      <PartnerDetailSheet
        u={selectedUni}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </section>
  );
};

export default PartnerDirectory;
